import { useState, useEffect, useMemo, useRef } from "react";
import type { ClipboardEvent as ReactClipboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import appIcon from "./app-icon.svg";
import "./App.css";

const THEME_STORAGE_KEY = "linkdrop-theme";

interface LinkItem {
  id: string;
  title: string;
  url: string;
  domain: string;
  faviconUrl: string;
  addedAt: string;
}

function normalizeUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }

    url.hash = "";

    if ((url.protocol === "https:" && url.port === "443") || (url.protocol === "http:" && url.port === "80")) {
      url.port = "";
    }

    if (url.pathname.endsWith("/") && url.pathname !== "/") {
      url.pathname = url.pathname.replace(/\/+$/, "");
    }

    return url.toString();
  } catch {
    return null;
  }
}

function getDomainTitle(domain: string): string {
  return domain.replace(/^www\./, "");
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return target.isContentEditable || tagName === "input" || tagName === "textarea" || tagName === "select";
}

function formatRelativeTime(dateString: string, now: number): string {
  const diffMs = now - new Date(dateString).getTime();
  const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));

  if (diffSeconds < 45) return "just now";

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  const diffWeeks = Math.floor(diffDays / 7);
  return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;
}

export function App() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  const [links, setLinks] = useState<LinkItem[]>([]);
  const [scrolled, setScrolled] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [themeFlash, setThemeFlash] = useState<"light" | "dark" | null>(null);
  const [isLoadingLinks, setIsLoadingLinks] = useState(true);
  const [removingLinkId, setRemovingLinkId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const themeFlashTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 30_000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    return () => {
      if (themeFlashTimeoutRef.current !== null) {
        window.clearTimeout(themeFlashTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    searchInputRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadLinks = async () => {
      try {
        const response = await fetch("/api/links", {
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load links: ${response.status}`);
        }

        const data = (await response.json()) as LinkItem[];
        if (!isCancelled) {
          setLinks(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Failed to load links:", error);
        if (!isCancelled) {
          setLinks([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingLinks(false);
        }
      }
    };

    void loadLinks();

    return () => {
      isCancelled = true;
    };
  }, []);

  const toggleTheme = () => {
    const newDark = !isDark;
    setIsDark(newDark);
    document.documentElement.classList.toggle("dark", newDark);
    window.localStorage.setItem(THEME_STORAGE_KEY, newDark ? "dark" : "light");

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    if (themeFlashTimeoutRef.current !== null) {
      window.clearTimeout(themeFlashTimeoutRef.current);
    }

    setThemeFlash(newDark ? "dark" : "light");
    themeFlashTimeoutRef.current = window.setTimeout(() => {
      setThemeFlash(null);
      themeFlashTimeoutRef.current = null;
    }, 180);
  };

  const addLinkFromText = async (rawText: string): Promise<boolean> => {
    const normalizedUrl = normalizeUrl(rawText);
    if (!normalizedUrl || links.some((link) => link.url === normalizedUrl)) {
      return false;
    }

    try {
      const response = await fetch("/api/links", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ url: normalizedUrl }),
      });

      if (response.status === 409) {
        return false;
      }

      if (!response.ok) {
        throw new Error(`Failed to add link: ${response.status}`);
      }

      const newLink = (await response.json()) as LinkItem;
      setLinks((currentLinks) => [newLink, ...currentLinks]);
      setSearchQuery("");
      searchInputRef.current?.focus({ preventScroll: true });
      return true;
    } catch (error) {
      console.error("Failed to add link:", error);
      return false;
    }
  };

  const handlePaste = async (event: ReactClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData("text");

    if (!(await addLinkFromText(pastedText))) {
      return;
    }

    event.preventDefault();
  };

  const removeLink = async (id: string) => {
    if (removingLinkId) {
      return;
    }

    setRemovingLinkId(id);

    try {
      const response = await fetch(`/api/links/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to remove link: ${response.status}`);
      }

      setLinks((currentLinks) => currentLinks.filter((link) => link.id !== id));
    } catch (error) {
      console.error("Failed to remove link:", error);
    } finally {
      setRemovingLinkId((currentId) => (currentId === id ? null : currentId));
    }
  };

  useEffect(() => {
    const handleWindowPaste = (event: ClipboardEvent) => {
      if (isEditableTarget(event.target)) {
        return;
      }

      const pastedText = event.clipboardData?.getData("text") ?? "";
      const addFromPaste = async () => {
        if (!(await addLinkFromText(pastedText))) {
          return;
        }

        event.preventDefault();
      };

      void addFromPaste();
    };

    window.addEventListener("paste", handleWindowPaste);
    return () => window.removeEventListener("paste", handleWindowPaste);
  }, [links]);

  const filteredLinks = useMemo(() => {
    return links.filter(
      (link) =>
        link.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        link.url.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [links, searchQuery]);

  const showEmptyState = !isLoadingLinks && filteredLinks.length === 0;

  return (
    <div className="app-container">
      <AnimatePresence>
        {themeFlash && (
          <motion.div
            key={themeFlash}
            className={`theme-transition-overlay theme-transition-overlay--${themeFlash}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </AnimatePresence>

      <header className={`header ${scrolled ? "header--scrolled" : ""}`}>
        <div className="header-inner">
          <div className="header-brand" aria-hidden="true">
            <img src={appIcon} alt="" className="header-brand-icon" />
          </div>
          <div className="search-container">
            <svg
              className="search-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search links..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onPaste={(event) => {
                void handlePaste(event);
              }}
            />
          </div>
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            <AnimatePresence mode="wait">
              {isDark ? (
                <motion.svg
                  key="moon"
                  initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                  transition={{ duration: 0.2 }}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.5 3.5c-1 1.3-1.5 3-1.5 4.8 0 4.5 3.7 8.2 8.2 8.2 1.2 0 2.4-.3 3.5-.8a9 9 0 1 1-10.2-12.2Z" />
                  <path d="M16.2 5.2v1.6" />
                  <path d="M15.4 6h1.6" />
                </motion.svg>
              ) : (
                <motion.svg
                  key="sun"
                  initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
                  transition={{ duration: 0.2 }}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2" />
                  <path d="M12 20v2" />
                  <path d="m4.93 4.93 1.41 1.41" />
                  <path d="m17.66 17.66 1.41 1.41" />
                  <path d="M2 12h2" />
                  <path d="M20 12h2" />
                  <path d="m6.34 17.66-1.41 1.41" />
                  <path d="m19.07 4.93-1.41 1.41" />
                </motion.svg>
              )}
            </AnimatePresence>
          </button>
        </div>
      </header>

      <main className="main-content">
        <ul className="link-list">
          <AnimatePresence initial={false}>
            {filteredLinks.map((link) => (
              <motion.li
                key={link.id}
                className="link-row"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.15 }}
              >
                <button
                  type="button"
                  className="link-remove"
                  aria-label={`Remove ${link.title}`}
                  onClick={() => {
                    void removeLink(link.id);
                  }}
                  disabled={removingLinkId === link.id}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-item"
                >
                  <div className="link-icon">
                    <img src={link.faviconUrl} alt="" loading="lazy" />
                  </div>
                  <div className="link-info">
                    <span className="link-title">{link.title}</span>
                    <span className="link-url">{link.url.replace(/^https?:\/\//, "")}</span>
                  </div>
                  <span className="link-age">{formatRelativeTime(link.addedAt, now)}</span>
                </a>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>

        <AnimatePresence initial={false}>
          {showEmptyState && (
            <motion.div
              key="empty-state"
              className="empty-state"
              initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -6, filter: "blur(3px)" }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              No links found matching your search.
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

export default App;
